import {
  CLASS_MODE_KEY,
  classModeFromSearch,
  parseClassCode,
  isTeacherKey,
} from "@greg-chick/shared";

/**
 * 📚 **수업 모드** — 이 탭에서만, 이 수업 동안만.
 *
 * ## 왜 탭에 담나
 * 선생님이 `?class=1` 링크를 나눠 주면 그 탭에서는 계속 수업 모드여야 한다.
 * 그런데 아이가 화면을 옮기면 주소에서 파라미터가 사라진다 — 그때마다 풀리면 소용없다.
 *
 * 🔴 **기기에 영구히 남기지 않는다.** 학교 태블릿을 집에 가져가거나 다음 시간 다른 수업에
 * 쓸 때 굿즈가 계속 안 보이면, 아무도 이유를 모른 채 「고장난 것」이 된다.
 * 탭을 닫으면 풀리는 자리(sessionStorage)가 맞다.
 */
let cached: boolean | null = null;

export function classMode(): boolean {
  if (cached !== null) return cached;
  // 주소에 분명히 적혀 있으면 그걸로 바꾸고 기억한다
  const fromUrl = classModeFromSearch(typeof location !== "undefined" ? location.search : "");
  if (fromUrl !== null) {
    cached = fromUrl;
    try {
      if (fromUrl) sessionStorage.setItem(CLASS_MODE_KEY, "1");
      else sessionStorage.removeItem(CLASS_MODE_KEY);
    } catch {
      // 저장이 막힌 브라우저 — 이 화면 동안은 메모리로 버틴다
    }
    return cached;
  }
  try {
    cached = sessionStorage.getItem(CLASS_MODE_KEY) === "1";
  } catch {
    cached = false;
  }
  return cached;
}

/** 테스트·설정 화면에서 끄고 켤 때. */
export function setClassMode(on: boolean): void {
  cached = on;
  try {
    if (on) sessionStorage.setItem(CLASS_MODE_KEY, "1");
    else sessionStorage.removeItem(CLASS_MODE_KEY);
  } catch {
    /* 메모리만 */
  }
}

/** 테스트용 — 캐시를 비운다. */
export function resetClassMode(): void {
  cached = null;
  cachedCode = undefined;
  cachedTeacher = undefined;
}

/* ── 반 코드 ──────────────────────────────────────────── */

/**
 * 이 탭이 들어가 있는 반.
 *
 * 🔴 **한 곳에만 둔다.** 화상 교실이 들고 있는 코드와 그림책이 올라가는 반이 갈리면,
 * 아이는 수업에 들어가 있는데 이야기는 아무 반에도 안 올라가는 상태가 된다 —
 * 화면 어디에도 오류가 안 뜨고, 선생님 화면만 텅 빈다.
 *
 * 수업 모드와 같은 자리(탭)에 담는다. 태블릿을 집에 가져가도 따라가지 않는다.
 */
const CLASS_CODE_KEY = "gc_class_code";
let cachedCode: string | null | undefined;

export function classCode(): string | null {
  if (cachedCode !== undefined) return cachedCode;
  try {
    cachedCode = parseClassCode(sessionStorage.getItem(CLASS_CODE_KEY) ?? "");
  } catch {
    cachedCode = null;
  }
  return cachedCode;
}

/** 코드가 코드 모양이 아니면 안 담는다 — 담아 봤자 서버가 400 을 줄 뿐이다. */
export function setClassCode(code: string | null): void {
  const clean = code ? parseClassCode(code) : null;
  cachedCode = clean;
  try {
    if (clean) sessionStorage.setItem(CLASS_CODE_KEY, clean);
    else sessionStorage.removeItem(CLASS_CODE_KEY);
  } catch {
    /* 메모리만 */
  }
}

/* ── 선생님 열쇠 ──────────────────────────────────────── */

/**
 * 이 탭이 선생님인가.
 *
 * 🔴 **탭에만 담는다.** 학교 공용 컴퓨터에서 브라우저를 닫으면 사라져야 한다.
 * 기기에 영구히 남기면 다음 시간 다른 사람이 그 반의 선생님이 된다.
 */
const TEACHER_KEY_KEY = "gc_teacher_key";
let cachedTeacher: string | null | undefined;

export function teacherKey(): string | null {
  if (cachedTeacher !== undefined) return cachedTeacher;
  try {
    const v = sessionStorage.getItem(TEACHER_KEY_KEY);
    cachedTeacher = isTeacherKey(v) ? v.toUpperCase() : null;
  } catch {
    cachedTeacher = null;
  }
  return cachedTeacher;
}

export function setTeacherKey(key: string | null): void {
  const clean = isTeacherKey(key) ? key.toUpperCase() : null;
  cachedTeacher = clean;
  try {
    if (clean) sessionStorage.setItem(TEACHER_KEY_KEY, clean);
    else sessionStorage.removeItem(TEACHER_KEY_KEY);
  } catch {
    /* 메모리만 */
  }
}
